import { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Sprout, X, Plus } from 'lucide-react';
import nurseryService from '../../services/nursery';
import type { NurseryTreeType, ReadinessMode, LeadTimeAnchor, TreeTypeUpdatePayload, CreateTreeTypePayload } from '../../types/nursery';

const EMPTY_CREATE: CreateTreeTypePayload = { code: '', name: '', stock_type: 'B', description: '', sort_order: 0 };

const MODE_LABEL: Record<ReadinessMode, string> = {
  lead_time: 'Lead time (offset from a date)',
  seasonal_window: 'Seasonal window (dig/ship season)',
  manual: 'Manual (set per lot)',
};

const ANCHOR_LABEL: Record<LeadTimeAnchor, string> = {
  pot_date: 'Pot date',
  plant_date: 'Plant date',
  graft_date: 'Graft date',
  last_movement: 'Last movement',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function readinessSummary(t: NurseryTreeType): string {
  if (t.readiness_mode === 'lead_time') {
    return t.lead_time_days != null
      ? `${t.lead_time_days} days after ${ANCHOR_LABEL[t.lead_time_anchor].toLowerCase()}`
      : 'Lead time (not set)';
  }
  if (t.readiness_mode === 'seasonal_window') {
    if (t.season_start_month) {
      const end = t.season_end_month || t.season_start_month;
      return `Season ${MONTHS[t.season_start_month - 1]}–${MONTHS[end - 1]}`;
    }
    return 'Seasonal window (not set)';
  }
  return 'Manual';
}

function TreeTypeAdmin() {
  const [treeTypes, setTreeTypes] = useState<NurseryTreeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<NurseryTreeType | null>(null);
  const [form, setForm] = useState<TreeTypeUpdatePayload>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateTreeTypePayload>(EMPTY_CREATE);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await nurseryService.getTreeTypes();
      if (res.success) setTreeTypes(res.data || []);
      else setError('Failed to load tree types');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load tree types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openEdit = (t: NurseryTreeType) => {
    setEditing(t);
    setForm({
      name: t.name,
      description: t.description,
      sort_order: t.sort_order,
      readiness_mode: t.readiness_mode,
      lead_time_days: t.lead_time_days,
      lead_time_anchor: t.lead_time_anchor,
      season_start_month: t.season_start_month,
      season_end_month: t.season_end_month,
    });
    setSaveError(null);
  };

  const openCreate = () => {
    setCreateForm(EMPTY_CREATE);
    setCreateError(null);
    setCreating(true);
  };

  const create = async () => {
    if (!createForm.code.trim() || !createForm.name.trim()) {
      setCreateError('Code and name are required');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      const res = await nurseryService.createTreeType({
        code: createForm.code.trim(),
        name: createForm.name.trim(),
        stock_type: createForm.stock_type,
        description: createForm.description?.trim() || undefined,
        sort_order: createForm.sort_order,
      });
      if (res.success) {
        setCreating(false);
        load();
      } else {
        setCreateError((res as { error?: { message?: string } }).error?.message || 'Failed to create');
      }
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setCreateSaving(false);
    }
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await nurseryService.updateTreeType(editing.id, form);
      if (res.success) {
        setEditing(null);
        load();
      } else {
        setSaveError((res as { error?: { message?: string } }).error?.message || 'Failed to save');
      }
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Unexpected error');
    } finally {
      setSaving(false);
    }
  };

  const isGlobal = editing?.organisation_id == null;

  return (
    <>
      <PageHeader
        title="Tree Types & Readiness Rules"
        subtitle="How a lot's ready date is calculated for each tree type"
        icon={<Sprout className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" /> New tree type
          </Button>
        }
      />
      <div className="max-w-5xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 mb-6">{error}</div>
        )}

        {loading ? (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => <div key={i} className="h-14 bg-neutral-100 rounded-lg animate-pulse" />)}
          </div>
        ) : treeTypes.length === 0 ? (
          <div className="bg-white border border-dashed border-neutral-300 rounded-xl px-6 py-12 text-center">
            <Sprout className="h-8 w-8 text-neutral-300 mx-auto mb-3" />
            <p className="text-sm text-neutral-600 mb-1">No tree types yet</p>
            <p className="text-xs text-neutral-400 mb-4">Create the tree types your nursery uses (e.g. bareroot grades and container sizes).</p>
            <Button variant="primary" size="sm" onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> New tree type
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-500 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">Code</th>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Type</th>
                  <th className="px-4 py-2 font-medium">Readiness</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {treeTypes.map(t => (
                  <tr key={t.id} className="hover:bg-neutral-50">
                    <td className="px-4 py-2 font-mono text-neutral-700">{t.code}</td>
                    <td className="px-4 py-2 text-neutral-800">{t.name}</td>
                    <td className="px-4 py-2">{t.stock_type === 'B' ? 'Bareroot' : 'Container'}</td>
                    <td className="px-4 py-2 text-neutral-600">{readinessSummary(t)}</td>
                    <td className="px-4 py-2 text-right">
                      <Button variant="outline" size="sm" onClick={() => openEdit(t)}>Edit</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {editing && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">Edit {editing.code}</h3>
                <button onClick={() => setEditing(null)}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              {isGlobal && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-700 mb-4">
                  This is a built-in global tree type and can't be edited. Create a new tree type if you need your own readiness rules.
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={form.name ?? ''}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={form.description ?? ''}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Sort order</label>
                  <input
                    type="number"
                    value={form.sort_order ?? 0}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, sort_order: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Readiness mode</label>
                  <select
                    value={form.readiness_mode}
                    disabled={isGlobal}
                    onChange={e => setForm(f => ({ ...f, readiness_mode: e.target.value as ReadinessMode }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm disabled:bg-neutral-100 disabled:text-neutral-400"
                  >
                    {(Object.keys(MODE_LABEL) as ReadinessMode[]).map(m => (
                      <option key={m} value={m}>{MODE_LABEL[m]}</option>
                    ))}
                  </select>
                </div>

                {form.readiness_mode === 'lead_time' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Lead time (days)</label>
                      <input
                        type="number" min="0"
                        value={form.lead_time_days ?? ''}
                        onChange={e => setForm(f => ({ ...f, lead_time_days: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Anchor date</label>
                      <select
                        value={form.lead_time_anchor}
                        onChange={e => setForm(f => ({ ...f, lead_time_anchor: e.target.value as LeadTimeAnchor }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        {(Object.keys(ANCHOR_LABEL) as LeadTimeAnchor[]).map(a => (
                          <option key={a} value={a}>{ANCHOR_LABEL[a]}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {form.readiness_mode === 'seasonal_window' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Season start</label>
                      <select
                        value={form.season_start_month ?? ''}
                        onChange={e => setForm(f => ({ ...f, season_start_month: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">—</option>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Season end</label>
                      <select
                        value={form.season_end_month ?? ''}
                        onChange={e => setForm(f => ({ ...f, season_end_month: e.target.value === '' ? null : parseInt(e.target.value, 10) }))}
                        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                      >
                        <option value="">—</option>
                        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                {saveError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{saveError}</div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setEditing(null)}>{isGlobal ? 'Close' : 'Cancel'}</Button>
                {!isGlobal && (
                  <Button variant="primary" size="sm" onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {creating && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-neutral-800">New tree type</h3>
                <button onClick={() => setCreating(false)}><X className="h-5 w-5 text-neutral-400 hover:text-neutral-600" /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Code</label>
                    <input
                      type="text" maxLength={20}
                      value={createForm.code}
                      onChange={e => setCreateForm(f => ({ ...f, code: e.target.value }))}
                      placeholder="e.g. P10L"
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Stock type</label>
                    <select
                      value={createForm.stock_type}
                      onChange={e => setCreateForm(f => ({ ...f, stock_type: e.target.value as 'B' | 'P' }))}
                      className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="B">Bareroot</option>
                      <option value="P">Container</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Name</label>
                  <input
                    type="text" maxLength={100}
                    value={createForm.name}
                    onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. 10L container"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={createForm.description ?? ''}
                    onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Sort order</label>
                  <input
                    type="number"
                    value={createForm.sort_order ?? 0}
                    onChange={e => setCreateForm(f => ({ ...f, sort_order: e.target.value === '' ? 0 : parseInt(e.target.value, 10) }))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <p className="text-xs text-neutral-500">
                  Set readiness rules (lead time / seasonal window) after creating, via “Edit”.
                </p>

                {createError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">{createError}</div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
                <Button variant="primary" size="sm" onClick={create} disabled={createSaving}>
                  {createSaving ? 'Creating…' : 'Create tree type'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default TreeTypeAdmin;
